import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Upload, FileText, X, Loader2, CheckCircle2, Copy, ShieldCheck, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { partnerCategories, IMG } from "../lib/data";
import { uploadFile, submitPartner, getVerifiedPartners } from "../lib/api";
import { isValidEmail, isValidPhone, sanitizePhone } from "../lib/validate";
import { trackEvent } from "../lib/analytics";
import PageHero from "../components/PageHero";

const INITIAL = {
  company_name: "", cr_number: "", contact_person: "", position: "", email: "",
  phone: "", whatsapp: "", country: "", city: "", website: "",
  categories: [], years_experience: "", company_description: "", files: [],
};

const PartnersPage = () => {
  const { t, lang } = useLanguage();
  const p = t.partnersPage;
  const fileRef = useRef(null);
  const [data, setData] = useState(INITIAL);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [verified, setVerified] = useState([]);

  useEffect(() => {
    getVerifiedPartners().then(setVerified).catch(() => setVerified([]));
  }, []);

  const upd = (k, v) => setData((s) => ({ ...s, [k]: v }));
  const toggleCat = (v) =>
    setData((s) => ({ ...s, categories: s.categories.includes(v) ? s.categories.filter((x) => x !== v) : [...s.categories, v] }));

  const emailErr = !!data.email && !isValidEmail(data.email);
  const phoneErr = !!data.phone && !isValidPhone(data.phone);
  const whatsappErr = !!data.whatsapp && !isValidPhone(data.whatsapp);

  const onFiles = async (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setUploading(true);
    try {
      for (const f of arr) {
        const res = await uploadFile(f);
        setData((s) => ({ ...s, files: [...s.files, { id: res.id, original_filename: res.original_filename, storage_path: res.storage_path, content_type: res.content_type, size: res.size }] }));
      }
      toast.success("Files uploaded");
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };
  const removeFile = (id) => upd("files", data.files.filter((f) => f.id !== id));

  const submit = async () => {
    if (!data.company_name || !data.contact_person) { toast.error(p.requiredFields); return; }
    if (!isValidEmail(data.email)) { toast.error(p.invalidEmail); return; }
    if (!isValidPhone(data.phone)) { toast.error(p.invalidPhone); return; }
    if (data.whatsapp && !isValidPhone(data.whatsapp)) { toast.error(p.invalidPhone); return; }
    if (data.categories.length === 0) { toast.error(p.selectCategory); return; }
    setSubmitting(true);
    try {
      const res = await submitPartner(data);
      trackEvent("partner_registration", { reference: res.reference, categories: data.categories.join(",") });
      setSuccess(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-[#0A1223] border border-white/12 text-white px-4 py-3 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-colors placeholder:text-slate-600";
  const Label = ({ children, required }) => (
    <span className="text-sm font-medium text-slate-300 mb-1.5 block">
      {children}{required && <span className="text-[#D4AF37] ms-1">*</span>}
    </span>
  );

  if (success) {
    return (
      <div className="min-h-screen grid place-items-center pt-32 pb-20 px-6" data-testid="partner-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="glass max-w-xl w-full p-10 text-center">
          <div className="w-20 h-20 rounded-full bg-[#D4AF37]/15 grid place-items-center mx-auto mb-6">
            <CheckCircle2 size={44} className="text-[#D4AF37]" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white">{p.successTitle}</h1>
          <p className="mt-4 text-slate-300 leading-relaxed">{p.successMsg}</p>
          <div className="mt-6 bg-[#0A1223] border border-white/12 p-4">
            <p className="section-label !text-slate-400 mb-1">{p.referenceLabel}</p>
            <div className="flex items-center justify-center gap-2">
              <span className="font-mono text-[#D4AF37] text-base" data-testid="partner-reference">{success.reference}</span>
              <button onClick={() => { navigator.clipboard.writeText(success.reference); toast.success("Copied"); }} className="text-slate-300 hover:text-white"><Copy size={15} /></button>
            </div>
          </div>
          <button onClick={() => { setSuccess(null); setData(INITIAL); }} data-testid="partner-new" className="btn-gold px-6 py-3 mt-8">
            {p.newRegistration}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="partners-page">
      <PageHero label={p.label} title={p.heading} sub={p.intro} image={IMG.siteWork} />

      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="glass p-7 md:p-10" data-testid="partner-form">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">{p.formHeading}</h2>

            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block"><Label required>{p.companyName}</Label>
                <input className={inputCls} value={data.company_name} onChange={(e) => upd("company_name", e.target.value)} data-testid="partner-company" /></label>
              <label className="block"><Label>{p.crNumber}</Label>
                <input className={inputCls} value={data.cr_number} onChange={(e) => upd("cr_number", e.target.value)} data-testid="partner-cr" /></label>
              <label className="block"><Label required>{p.contactPerson}</Label>
                <input className={inputCls} value={data.contact_person} onChange={(e) => upd("contact_person", e.target.value)} data-testid="partner-contact" /></label>
              <label className="block"><Label>{p.position}</Label>
                <input className={inputCls} value={data.position} onChange={(e) => upd("position", e.target.value)} data-testid="partner-position" /></label>
              <label className="block"><Label required>{p.email}</Label>
                <input type="email" className={inputCls} value={data.email} onChange={(e) => upd("email", e.target.value)} data-testid="partner-email" />
                {emailErr && <p className="text-xs text-red-400 mt-1.5" data-testid="partner-email-error">{p.invalidEmail}</p>}</label>
              <label className="block"><Label required>{p.phone}</Label>
                <input inputMode="tel" placeholder="+966549747549" className={inputCls} value={data.phone} onChange={(e) => upd("phone", sanitizePhone(e.target.value))} data-testid="partner-phone" />
                {phoneErr && <p className="text-xs text-red-400 mt-1.5" data-testid="partner-phone-error">{p.invalidPhone}</p>}</label>
              <label className="block"><Label>{p.whatsapp}</Label>
                <input inputMode="tel" placeholder="+966549747549" className={inputCls} value={data.whatsapp} onChange={(e) => upd("whatsapp", sanitizePhone(e.target.value))} data-testid="partner-whatsapp" />
                {whatsappErr && <p className="text-xs text-red-400 mt-1.5" data-testid="partner-whatsapp-error">{p.invalidPhone}</p>}</label>
              <label className="block"><Label>{p.website}</Label>
                <input placeholder="https://" className={inputCls} value={data.website} onChange={(e) => upd("website", e.target.value)} data-testid="partner-website" /></label>
              <label className="block"><Label required>{p.country}</Label>
                <input className={inputCls} value={data.country} onChange={(e) => upd("country", e.target.value)} data-testid="partner-country" /></label>
              <label className="block"><Label>{p.city}</Label>
                <input className={inputCls} value={data.city} onChange={(e) => upd("city", e.target.value)} data-testid="partner-city" /></label>
            </div>

            {/* Business category */}
            <div className="mt-7">
              <Label required>{p.categoriesLabel}</Label>
              <p className="text-xs text-slate-400 mb-3">{p.categoriesHint}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {partnerCategories.map((c) => {
                  const active = data.categories.includes(c.v);
                  return (
                    <button type="button" key={c.v} onClick={() => toggleCat(c.v)} data-testid={`partner-cat-${c.v}`}
                      className={`relative text-start px-4 py-3 border text-sm font-medium transition-all duration-200 ${active ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A1223]" : "bg-[#0A1223] border-white/12 text-slate-200 hover:border-[#D4AF37]/60"}`}>
                      {c[lang]}
                      {active && <Check size={15} className="absolute top-2.5 end-2.5" />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-7 grid sm:grid-cols-2 gap-5">
              <label className="block"><Label>{p.yearsExperience}</Label>
                <input inputMode="numeric" className={inputCls} value={data.years_experience} onChange={(e) => upd("years_experience", e.target.value.replace(/[^\d]/g, "").slice(0, 3))} data-testid="partner-years" /></label>
            </div>

            <label className="block mt-5"><Label>{p.companyDescription}</Label>
              <textarea rows={5} className={inputCls} value={data.company_description} onChange={(e) => upd("company_description", e.target.value)} data-testid="partner-description" /></label>

            {/* Documents */}
            <div className="mt-7">
              <Label>{p.docsLabel}</Label>
              <p className="text-xs text-slate-400 mb-3">{p.docsHint}</p>
              <div onClick={() => fileRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
                data-testid="partner-dropzone" className="border-2 border-dashed border-white/15 hover:border-[#D4AF37]/60 transition-colors p-8 text-center cursor-pointer">
                {uploading ? <Loader2 size={30} className="mx-auto text-[#D4AF37] animate-spin" /> : <Upload size={30} className="mx-auto text-[#D4AF37]" />}
                <p className="mt-3 text-slate-200 font-medium text-sm">{uploading ? p.uploading : p.dropFiles}</p>
                <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="partner-file-input" />
              </div>
              {data.files.length > 0 && (
                <div className="mt-4 space-y-2.5" data-testid="partner-file-list">
                  {data.files.map((f) => (
                    <div key={f.id} className="flex items-center gap-3 bg-[#0A1223] border border-white/12 px-4 py-3">
                      <FileText size={18} className="text-[#D4AF37] shrink-0" />
                      <span className="text-sm text-slate-200 truncate flex-1">{f.original_filename}</span>
                      <span className="text-xs text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
                      <button onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-red-400"><X size={16} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 flex items-start gap-3 bg-[#11203A] border border-[#D4AF37]/20 p-4">
              <ShieldCheck size={18} className="text-[#D4AF37] shrink-0 mt-0.5" />
              <p className="text-xs text-slate-300 leading-relaxed">{p.successMsg}</p>
            </div>

            <button onClick={submit} disabled={submitting} data-testid="partner-submit" className="btn-gold mt-6 inline-flex items-center gap-2 px-7 py-3.5 disabled:opacity-50">
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
              {p.submit}
            </button>
          </div>
        </div>
      </section>

      {/* Verified partners */}
      <section className="py-16 md:py-24 bg-[#11203A] border-y border-white/5" data-testid="verified-partners-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white text-center">{p.verifiedHeading}</h2>
          <p className="mt-4 text-slate-300 text-center max-w-2xl mx-auto">{p.verifiedSub}</p>
          {verified.length === 0 ? (
            <p className="mt-12 text-center text-slate-400" data-testid="verified-empty">{p.verifiedEmpty}</p>
          ) : (
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {verified.map((v) => (
                <div key={v.partner_id} className="bg-[#0A1223] border border-white/12 p-6" data-testid={`verified-card-${v.partner_id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[#D4AF37] text-sm">{v.partner_id}</span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400"><BadgeCheck size={15} /> {p.statusVerified}</span>
                  </div>
                  <div className="mt-4 space-y-1.5 text-sm">
                    <p className="text-slate-400">{p.colCategory}: <span className="text-slate-100">{v.category}</span></p>
                    <p className="text-slate-400">{p.colCountry}: <span className="text-slate-100">{v.country}</span></p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;
